import { useMemo, useEffect, useRef, useState } from 'react'

import type { useQuery } from '@apollo/client/react'
import { tz } from '@date-fns/tz'
import { Temporal } from '@js-temporal/polyfill'
import { endOfWeek, eachWeekOfInterval } from 'date-fns'
import type { Day } from 'date-fns'
import { useSplitEnabled } from '@buffer-mono/features'

import { buildTagComparator } from '~publish/components/FilterByTag'
import type { PostStatus } from '~publish/components/graphqlEnums'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { getDateRange } from '~publish/helpers/temporal'
import { useLazyManyQueries } from '~publish/hooks/useLazyManyQueries'
import type {
  ManyQueriesResult,
  QueryConfig,
  SingleQueryResult,
} from '~publish/hooks/useLazyManyQueries'
import type { FilterablePostType } from '~publish/helpers/postType'
import { useTimezone } from '~publish/hooks/useTimezone'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'
import { CalendarPostCard_Post } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'
import type { CalendarPostCard_PostData } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'

import { useCalendarRealTimeUpdates } from './useCalendarRealTimeUpdates'
import { COLLABORATION_NOTES_SPLIT } from '~publish/components/PostDialogs/PostDetailsContent/useCollaborationNotesPanel'
import { PostUnreadNoteCount } from '~publish/hooks/notes/fragments/PostUnreadNoteCount'

// Capped at the backend's per-page limit for external clients. See PR #8094
// (initial 100), PR #8693 (bumped to 200 with weekly segmentation), INC-385
// (revert), and PR #19041 (back to 100).
const POST_LIMIT = 100

export const GetCalendarAndPostList = graphql(
  `
    query GetCalendarAndPostList(
      $startDate: DateTime!
      $endDate: DateTime!
      $channelIds: [ChannelId!]
      $organizationId: OrganizationId!
      $postsLimit: Int!
      $tags: TagComparator
      $status: [PostStatus!]
      $after: String
      $postTypes: [PostType!]
      $includeNotesSummary: Boolean! = false
    ) {
      posts(
        first: $postsLimit
        after: $after
        input: {
          organizationId: $organizationId
          filter: {
            channelIds: $channelIds
            dueAt: { start: $startDate, end: $endDate }
            tags: $tags
            status: $status
            postTypes: $postTypes
          }
          sort: [
            { field: dueAt, direction: asc }
            { field: createdAt, direction: desc }
          ]
        }
      ) {
        edges {
          node {
            id
            dueAt
            ...CalendarPostCard_Post
            ...PostUnreadNoteCount @include(if: $includeNotesSummary)
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `,
  [CalendarPostCard_Post, PostUnreadNoteCount],
)

export type QueryVariables = VariablesOf<typeof GetCalendarAndPostList>
export type Data = ResultOf<typeof GetCalendarAndPostList>
type CalendarQuery = QueryConfig<QueryVariables> & {
  variables: QueryVariables
}

export type PostForCalendarHookResponse = {
  data: Data | undefined
  loading: boolean
  refetch: () => void
  variables: QueryVariables
}

type PostForCalendarHookProps = {
  currentDate: number
  /**
   * Calendar display mode.
   * @see services/publish/publish-frontend/src/pages/Calendar/hooks/useNavigation.ts (CalendarGranularity)
   */
  calendarMode: 'week' | 'month' | 'three-day'
  channelsSelected: string[] | undefined
  tagIds: string[] | undefined
  status: PostStatus[] | undefined
  /** Post formats to filter by. Omit or pass an empty list for every format. */
  postTypes?: readonly FilterablePostType[]
}

// TODO replace with fragment
type NewPostType = NonNullable<Data['posts']['edges']>[number]['node']

export const asPostsFragment = (
  data: Data | null | undefined,
): (NewPostType | undefined | null)[] => {
  return data?.posts.edges?.map((edge) => edge.node) ?? []
}

export const usePostsFragment = asPostsFragment

export const getPosts = (
  data: Data | null | undefined,
): CalendarPostCard_PostData[] => {
  return (
    data?.posts.edges?.map((edge) =>
      readFragment(CalendarPostCard_Post, edge.node),
    ) ?? []
  )
}

/**
 * Hook that manages the data fetching for calendar posts view.
 * Integrates with Redux for refetch signaling and feature flags for query behavior.
 *
 * - Calculates date ranges based on calendar mode
 * - Handles post fetching with pagination and interval generation
 * - Manages refetch cycles through Redux state monitoring
 *
 * @param {Object} props - Calendar view configuration
 * @param {number} props.currentDate - Unix timestamp for current view's date
 * @param {PostForCalendarHookProps['calendarMode']} props.calendarMode - Calendar display mode
 * @see services/publish/publish-frontend/src/pages/Calendar/hooks/useNavigation.ts (CalendarGranularity)
 * @param {string[]|undefined} props.channelsSelected - Array of channel IDs to filter by
 * @param {string[]|undefined} props.tagIds - Array of tag IDs to filter by
 * @param {string[]|undefined} props.status - Array of post statuses to filter by
 *
 * @returns {Object} Calendar data and controls
 * @returns {Data|undefined} returns.data - GraphQL query results
 * @returns {IntervalsResponse} returns.intervals - Time intervals for the calendar grid
 * @returns {boolean} returns.loading - Loading state for post data
 * @returns {Function} returns.refetch - Trigger to reload post data
 * @returns {QueryVariables} returns.variables - Current GraphQL variables
 *
 * @effect Monitors Redux state for refetch signals
 * @effect Dispatches Redux action to clear refetch flag after reload
 * @dependencies Uses organization context, browser timezone, and feature flags
 */
export const useCalendarAndPostsList = (
  props: PostForCalendarHookProps,
): PostForCalendarHookResponse => {
  const {
    currentDate,
    channelsSelected,
    calendarMode,
    tagIds,
    status,
    postTypes,
  } = props
  const timeZone = useTimezone()
  const currentDateZoned =
    Temporal.Instant.fromEpochMilliseconds(currentDate).toZonedDateTimeISO(
      timeZone,
    )
  const weekStartsOn = useWeekStartsOn() === 'monday' ? 1 : 0
  const range = getDateRange(currentDateZoned, calendarMode, weekStartsOn)

  const [startDate, endDate] = [
    range.start.toInstant().toString(),
    range.end.toInstant().toString(),
  ]

  const organizationId = useOrganizationId()
  const { isReady, isEnabled } = useSplitEnabled(COLLABORATION_NOTES_SPLIT)

  const variables = useVariables({
    startDate,
    endDate,
    organizationId,
    channelsSelected: channelsSelected ?? undefined,
    tagIds: tagIds ?? undefined,
    status: status ?? undefined,
    postTypes: postTypes ?? undefined,
    includeNotesSummary: isReady && isEnabled,
  })

  const adjacentVariables = useMemo(() => {
    if (calendarMode !== 'month') return []

    const selectedDate =
      Temporal.Instant.fromEpochMilliseconds(currentDate).toZonedDateTimeISO(
        timeZone,
      )

    const getAdjacentVariables = (monthOffset: -1 | 1): QueryVariables => {
      const adjacentRange = getDateRange(
        selectedDate.add({ months: monthOffset }),
        calendarMode,
        weekStartsOn,
      )

      return {
        ...variables,
        startDate: adjacentRange.start.toInstant().toString(),
        endDate: adjacentRange.end.toInstant().toString(),
      }
    }

    return [getAdjacentVariables(-1), getAdjacentVariables(1)]
  }, [calendarMode, currentDate, timeZone, variables, weekStartsOn])

  const { data, loading, refetch } = useClientPaginatedPostList(
    variables,
    calendarMode,
    adjacentVariables,
  )

  useCalendarRealTimeUpdates({
    filters: {
      channelIds: channelsSelected ?? [],
      tagIds: tagIds ?? [],
      status: status ?? [],
    },
    refetch: async (): Promise<void> => {
      await refetch()
    },
    isPostVisible: (id) => {
      return data?.posts?.edges?.some((edge) => edge.node.id === id) ?? false
    },
    dateRange: {
      startDate: variables.startDate,
      endDate: variables.endDate,
    },
  })

  return useMemo(
    () => ({
      data,
      loading,
      refetch,
      variables,
    }),
    [data, loading, refetch, variables],
  )
}

type ClientPaginatedPostListResult = Omit<
  useQuery.Result<Data, QueryVariables>,
  | 'observable'
  | 'startPolling'
  | 'stopPolling'
  | 'subscribeToMore'
  | 'updateQuery'
  | 'reobserve'
  | 'fetchMore'
  | 'refetch'
  | 'data'
  | 'dataState'
> & {
  // mergeResults always produces a fully-formed query result, so the data is
  // the concrete query shape rather than the dataState partial union.
  data: Data
  refetch:
    | ManyQueriesResult<Data, QueryVariables>['refetch']
    | useQuery.Result<Data, QueryVariables>['refetch']
}

/**
 * Hook implementing a Date-Segmented strategy for calendar post fetching.
 *
 * Date-Segmented Mode:
 * - Splits month and week ranges into weekly segments
 * - Uses a single exact interval for three-day ranges
 * - Initial fetch of up to the backend-safe page size per interval
 * - Background fetch more posts per interval when hasNextPage is true
 * - Deduplicates posts by ID when merging results
 *
 * @param {Object} variables - GraphQL query variables
 * @param {string} variables.startDate - ISO string start date
 * @param {string} variables.endDate - ISO string end date
 * @param {string} variables.organizationId - Organization identifier
 * @param {string[]|undefined} variables.channelIds - Channel filters
 * @param {string[]|undefined} variables.tagIds - Tag filters
 * @param {string[]|undefined} variables.status - Status filters
 * @param {QueryVariables[]} adjacentVariables - Adjacent month ranges to prepare
 *
 * @returns {Object} Combined query results
 * @returns {Data} returns.data - Merged post data
 * @returns {boolean} returns.loading - Loading state
 * @returns {QueryVariables} returns.variables - Current query variables
 * @returns {Function} returns.refetch - Function to reload data
 *
 * @effect Initiates background fetches for more pages and adjacent months
 */
function useClientPaginatedPostList(
  variables: QueryVariables,
  calendarMode: PostForCalendarHookProps['calendarMode'],
  adjacentVariables: QueryVariables[],
): ClientPaginatedPostListResult {
  const weekStartsOn: Day = useWeekStartsOn() === 'monday' ? 1 : 0
  const timezone = useTimezone()
  const [manyQueriesResultsOnCompleted, setManyQueriesResultsFirstLoad] =
    useState<SingleQueryResult<Data, QueryVariables>[]>([])
  const [completedVariables, setCompletedVariables] = useState<QueryVariables>()
  const [firstLoad, setFirstLoad] = useState(true)
  const requestedPageKeys = useRef(new Set<string>())
  const [
    executeQueries,
    { results: manyQueriesResults, refetch, client, ...manyQueriesResultRest },
  ] = useLazyManyQueries<Data, QueryVariables>({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    onCompleted: ({ results }) => {
      setManyQueriesResultsFirstLoad(results)
      setCompletedVariables(variables)
      setFirstLoad(false)
    },
  })

  const currentQueries = useMemo(
    () => getCalendarQueries(variables, calendarMode, weekStartsOn, timezone),
    [calendarMode, timezone, variables, weekStartsOn],
  )
  const adjacentQueries = useMemo(
    () =>
      adjacentVariables.flatMap((adjacentRangeVariables) =>
        getCalendarQueries(
          adjacentRangeVariables,
          calendarMode,
          weekStartsOn,
          timezone,
        ),
      ),
    [adjacentVariables, calendarMode, timezone, weekStartsOn],
  )

  useEffect(() => {
    if (!variables.organizationId) return

    requestedPageKeys.current.clear()
    void executeQueries(currentQueries)
  }, [currentQueries, executeQueries, variables.organizationId])

  useEffect(() => {
    if (
      calendarMode !== 'month' ||
      completedVariables !== variables ||
      manyQueriesResultRest.error
    ) {
      return
    }

    const idleCallback = requestIdleCallback(() => {
      void Promise.all(
        adjacentQueries.map(({ query, variables: queryVariables }) =>
          client
            .query<Data, QueryVariables>({
              query,
              variables: queryVariables,
              fetchPolicy: 'cache-first',
            })
            .catch(() => undefined),
        ),
      )
    })

    return (): void => cancelIdleCallback(idleCallback)
  }, [
    adjacentQueries,
    calendarMode,
    client,
    completedVariables,
    manyQueriesResultRest.error,
    variables,
  ])

  useEffect(
    function fetchMoreIfNeeded() {
      // Fetch more posts for queries that have a next page
      const idleCallback = requestIdleCallback(() => {
        manyQueriesResults
          .filter(
            (res) =>
              res?.data?.posts.pageInfo.hasNextPage &&
              res.data.posts.pageInfo.endCursor,
          )
          .forEach(({ fetchMore, variables: queryVariables, data }) => {
            const endCursor = data?.posts.pageInfo.endCursor
            const startDate = queryVariables?.startDate
            const endDate = queryVariables?.endDate

            if (!endCursor || !startDate || !endDate) {
              return
            }

            const pageKey = `${startDate}:${endDate}:${endCursor}`

            if (requestedPageKeys.current.has(pageKey)) {
              return
            }

            requestedPageKeys.current.add(pageKey)
            void fetchMore({
              variables: {
                ...queryVariables,
                after: endCursor,
              },
              updateQuery: (previousResult, { fetchMoreResult }) => ({
                ...previousResult,
                posts: {
                  ...previousResult.posts,
                  edges: [
                    ...(previousResult.posts.edges ?? []),
                    ...(fetchMoreResult.posts.edges ?? []),
                  ],
                  pageInfo: fetchMoreResult.posts.pageInfo,
                },
              }),
            }).catch(() => {
              requestedPageKeys.current.delete(pageKey)
            })
          })
      })
      return (): void => cancelIdleCallback(idleCallback)
    },
    [manyQueriesResults],
  )

  const waitingForCurrentRange =
    manyQueriesResultRest.loading ||
    (!manyQueriesResultRest.error && completedVariables !== variables)
  const cachedCurrentData =
    calendarMode === 'month' && waitingForCurrentRange
      ? currentQueries.map(({ query, variables: queryVariables }) =>
          client.readQuery<Data, QueryVariables>({
            query,
            variables: queryVariables,
            optimistic: true,
          }),
        )
      : []
  const hasDataForCurrentRange =
    currentQueries.length > 0 &&
    cachedCurrentData.every((data) => data !== null && data !== undefined)
  const canUseCurrentRangeCache =
    calendarMode === 'month' && waitingForCurrentRange && hasDataForCurrentRange

  return {
    data: canUseCurrentRangeCache
      ? mergeData(cachedCurrentData)
      : mergeResults(
          // To avoid days loading one by one, we use the results from the
          // onCompleted hook on the first load. After the first load, we use the
          // results from the useLazyManyQueries hook to take advantage of the cache.
          firstLoad ? manyQueriesResultsOnCompleted : manyQueriesResults,
        ),
    variables,
    ...manyQueriesResultRest,
    client,
    refetch: async (refetchVariables?: Partial<QueryVariables>[]) => {
      requestedPageKeys.current.clear()
      return refetch(refetchVariables)
    },
    loading: waitingForCurrentRange,
  }
}

function getCalendarQueries(
  variables: QueryVariables,
  calendarMode: PostForCalendarHookProps['calendarMode'],
  weekStartsOn: Day,
  timezone: string,
): CalendarQuery[] {
  const dates =
    calendarMode === 'three-day'
      ? [{ start: variables.startDate, end: variables.endDate }]
      : getWeeksInRange(
          new Date(variables.startDate),
          new Date(variables.endDate),
          weekStartsOn,
          timezone,
        )

  return dates.map((date) => ({
    query: GetCalendarAndPostList,
    variables: {
      ...variables,
      startDate: date.start,
      endDate: date.end,
      postsLimit: POST_LIMIT,
    },
  }))
}

function mergeResults(
  results: SingleQueryResult<Data, QueryVariables>[],
): Data {
  return mergeData(results.map((result) => result.data))
}

function mergeData(results: readonly (Data | null | undefined)[]): Data {
  const edges = results.flatMap((data) => data?.posts.edges ?? [])

  const uniqueEdgesById = new Map<string, (typeof edges)[number]>()

  for (const edge of edges) {
    if (!uniqueEdgesById.has(edge.node.id)) {
      uniqueEdgesById.set(edge.node.id, edge)
    }
  }

  const uniqueEdges = Array.from(uniqueEdgesById.values())
  return {
    posts: {
      edges: uniqueEdges,
      pageInfo: {
        hasNextPage: false,
        endCursor: null,
      },
    },
  }
}

/**
 * Custom hook that generates stable query variables for the GraphQL query.
 * Implements memoization to prevent unnecessary re-renders and query refetches.
 * Uses a stable key generation strategy for arrays by sorting and joining values.
 */
function useVariables(variables: {
  startDate: string
  endDate: string
  organizationId: string | undefined | null
  channelsSelected?: string[] | null
  tagIds?: string[] | null
  status?: PostStatus[] | null
  postTypes?: readonly FilterablePostType[] | null
  includeNotesSummary: boolean
}): QueryVariables {
  const {
    startDate,
    endDate,
    organizationId,
    channelsSelected,
    tagIds,
    status,
    postTypes,
    includeNotesSummary,
  } = variables
  return useMemo<QueryVariables>(
    () => ({
      startDate,
      endDate,
      organizationId: organizationId ?? '',
      postsLimit: POST_LIMIT,
      channelIds: channelsSelected ?? undefined,
      tags: buildTagComparator(tagIds ?? undefined),
      status: status ?? undefined,
      postTypes: postTypes?.length ? [...postTypes] : undefined,
      includeNotesSummary,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // HACK: use a stable key to avoid recreating the variables object
      // TODO: Review the creation of filters on useCalendarState since it is not stable
      startDate,
      endDate,
      organizationId,
      includeNotesSummary,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      channelsSelected?.toSorted().join(','),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      tagIds?.toSorted().join(','),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      status?.toSorted().join(','),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      postTypes?.toSorted().join(','),
    ],
  )
}

/**
 * Generates a list of week date ranges in the range between startDate and endDate.
 * @param startDate - Start date
 * @param endDate - End date
 * @param weekStartsOn - Week start day
 * @returns List of dates in range as { start: string, end: string }
 */
function getWeeksInRange(
  startDate: Date,
  endDate: Date,
  weekStartsOn: Day,
  timezone: string,
): Array<{ start: string; end: string }> {
  const dates: Array<{ start: string; end: string }> = []
  const weeks = eachWeekOfInterval(
    {
      start: startDate,
      end: endDate,
    },
    {
      weekStartsOn,
      in: tz(timezone),
    },
  )

  weeks.forEach((weekStart) => {
    dates.push({
      start: weekStart.toISOString(),
      end: endOfWeek(weekStart, { weekStartsOn }).toISOString(),
    })
  })

  return dates
}
